'use client'

import { useT, useFormat, type TranslateFn } from '@/lib/i18n'

import { useState } from 'react'
import { useAnomalyList } from '../anomalies.hooks'
import type { AnomalyRow } from '../anomalies.types'
import {
  formatSchemeProbability,
  SCHEME_PROBABILITY_HINT_KEY,
  SCHEME_PROBABILITY_SHORT_LABEL_KEY,
} from '../scheme-probability'

function scoreColor(s: number): string {
  if (s >= 7) return 'var(--risk-high, #ef4444)'
  if (s >= 3) return 'var(--risk-medium, #f59e0b)'
  return 'var(--risk-low, #22c55e)'
}

// A.Reasons понятным языком для руководителя: технические коды IF/Z/IQR
// заменяются человеческими подписями. API-поля a_reasons/short_causes не меняются.
function humanReasons(raw: string | undefined, t: TranslateFn): string {
  if (!raw) return ''
  return raw
    .split(';')
    .map(s => s.trim())
    .filter(Boolean)
    .map(seg => {
      if (seg === 'IF') return t('analytics.method.atypicalCombo')
      if (seg === 'Z') return t('analytics.method.deviationFromMean')
      if (seg === 'IQR') return t('analytics.method.outOfRange')
      if (/^Δвес/i.test(seg)) return seg.replace(/^Δвес/i, t('analytics.method.weightDiff'))
      if (/^инт\./i.test(seg)) return seg.replace(/^инт\./i, t('analytics.method.interval'))
      return seg
    })
    .join('; ')
}

function levelBadge(l: string, t: TranslateFn) {
  const map: Record<string, { bg: string; fg: string; labelKey: string }> = {
    high: { bg: 'rgba(239,68,68,.12)', fg: '#ef4444', labelKey: 'common.risk.high' },
    medium: { bg: 'rgba(245,158,11,.12)', fg: '#f59e0b', labelKey: 'common.risk.medium' },
    low: { bg: 'rgba(34,197,94,.12)', fg: '#22c55e', labelKey: 'common.risk.low' },
  }
  const m = map[l] || map.low
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold"
      style={{ background: m.bg, color: m.fg }}
    >
      {t(m.labelKey)}
    </span>
  )
}

interface Props {
  onSelectCase: (caseId: string) => void
  excludeEaeu: boolean
  onExcludeEaeuChange: (v: boolean) => void
  mlLevel: string
  onMlLevelChange: (v: string) => void
  hideEmptyRoute: boolean
  onHideEmptyRouteChange: (v: boolean) => void
  aScoreLevel: string
  onAScoreLevelChange: (v: string) => void
  search: string
  onSearchChange: (v: string) => void
  debouncedSearch: string
  scenarioId: number | 'all'
}

const PAGE_SIZE = 50

export function AnomalyTable({ onSelectCase, excludeEaeu, onExcludeEaeuChange, mlLevel, onMlLevelChange, hideEmptyRoute, onHideEmptyRouteChange, aScoreLevel, onAScoreLevelChange, search, onSearchChange, debouncedSearch, scenarioId }: Props) {
  const t = useT()
  const f = useFormat()
  const [page, setPage] = useState(0)

  // Селектор «Сценарий» живёт в шапке страницы (вне фильтров таблицы) → при его
  // смене сбрасываем пагинацию, иначе можно застрять на несуществующей странице.
  // Сброс derived-state прямо в рендере (React-паттерн), без useEffect.
  const [prevScenarioId, setPrevScenarioId] = useState(scenarioId)
  if (scenarioId !== prevScenarioId) {
    setPrevScenarioId(scenarioId)
    setPage(0)
  }

  const { data, isLoading } = useAnomalyList({
    level: aScoreLevel || undefined,
    search: debouncedSearch || undefined,
    ml_level: mlLevel !== 'all' ? mlLevel : undefined,
    exclude_eaeu: excludeEaeu,
    hide_empty_route: hideEmptyRoute,
    scenario_id: scenarioId !== 'all' ? scenarioId : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder={t('analytics.table.searchPlaceholder')}
          value={search}
          onChange={e => { onSearchChange(e.target.value); setPage(0) }}
          className="h-8 w-64 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-3 text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-data)] focus:outline-none"
        />
        <select
          value={aScoreLevel}
          onChange={e => { onAScoreLevelChange(e.target.value); setPage(0) }}
          className="h-8 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)]"
        >
          <option value="">{t('analytics.table.aScoreAll')}</option>
          <option value="high">{t('analytics.table.aScoreHigh')}</option>
          <option value="medium">{t('analytics.table.aScoreMid')}</option>
          <option value="low">{t('analytics.table.aScoreLow')}</option>
        </select>
        <select
          value={mlLevel}
          onChange={e => { onMlLevelChange(e.target.value); setPage(0) }}
          className="h-8 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)]"
        >
          <option value="all">{t('analytics.table.mlAll')}</option>
          <option value="high">{t('analytics.table.mlHigh')}</option>
          <option value="medium">{t('analytics.table.mlMid')}</option>
          <option value="low">{t('analytics.table.mlLow')}</option>
        </select>
        <label className="flex h-8 cursor-pointer items-center gap-2 text-[12px] text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={excludeEaeu}
            onChange={e => { onExcludeEaeuChange(e.target.checked); setPage(0) }}
            className="h-3.5 w-3.5 accent-[var(--accent-data)]"
          />
          {t('analytics.table.hideEaeuRoutes')}
        </label>
        <label className="flex h-8 cursor-pointer items-center gap-2 text-[12px] text-[var(--text-secondary)]">
          <input
            type="checkbox"
            checked={hideEmptyRoute}
            onChange={e => { onHideEmptyRouteChange(e.target.checked); setPage(0) }}
            className="h-3.5 w-3.5 accent-[var(--accent-data)]"
          />
          {t('analytics.table.hideEmptyRoutes')}
        </label>
        {data && (
          <span className="text-[11px] text-[var(--text-tertiary)]">
            {t('analytics.table.totalCases', { count: data.total })}
          </span>
        )}
        {/* Бейдж модели, дающей колонку «Вер. схемы»: страница аномалий — это
            AnomaliesView, а scheme_model приходит в ответе списка, поэтому бейдж
            живёт здесь, в шапке витрины (рядом со счётчиком), а не тянется вверх
            пропсами. Подпись именно «вероятность схемы», а не «скоринг»: колонку
            ML эта модель больше не формирует — там исходный балл витрины. */}
        {data?.scheme_model && (
          <span
            title={t(SCHEME_PROBABILITY_HINT_KEY)}
            className="inline-block rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]"
          >
            вероятность схемы: {data.scheme_model.name}
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-[6px] border border-[var(--border-default)]">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="border-b border-[var(--border-default)] bg-[var(--bg-surface)]">
              {['ID', t('analytics.col.importDate'), t('analytics.col.exportDate'), 'A.Score', t('analytics.table.colLevel'), 'ML', t(SCHEME_PROBABILITY_SHORT_LABEL_KEY), t('common.field.cargo'), t('common.field.route'), t('analytics.col.weightDelta'), t('analytics.col.days'), t('common.field.scenario'), t('analytics.table.colMatch'), t('analytics.col.methods'), 'A.Reasons'].map(h => (
                <th key={h} className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.06em] text-[var(--text-tertiary)]">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={15} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('common.state.loading')}</td></tr>
            )}
            {data?.rows.map((r: AnomalyRow) => (
              <tr
                key={r.case_id}
                onClick={() => onSelectCase(r.case_id)}
                className="cursor-pointer border-b border-[var(--border-default)] transition-colors hover:bg-[var(--bg-surface)]"
              >
                <td className="whitespace-nowrap px-3 py-2 font-semibold text-[var(--accent-data)]">{r.case_id}</td>
                <td className="whitespace-nowrap px-3 py-2">{r.import_date || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2">{r.export_date || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2 font-bold" style={{ color: scoreColor(r.anomaly_score) }}>
                  {r.anomaly_score.toFixed(1)}
                </td>
                <td className="px-3 py-2">{levelBadge(r.anomaly_level, t)}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.ml_risk_score != null ? r.ml_risk_score.toFixed(1) : '—'}
                </td>
                {/* Вероятность схемной модели — своя колонка и свой масштаб.
                    В колонку ML она больше не попадает: это разные модели. */}
                <td className="whitespace-nowrap px-3 py-2 text-[var(--text-secondary)]" title={t(SCHEME_PROBABILITY_HINT_KEY)}>
                  {formatSchemeProbability(r.scheme_model_probability, f)}
                </td>
                <td className="max-w-[140px] truncate px-3 py-2" title={r.cargo}>{r.cargo || '—'}</td>
                <td className="max-w-[120px] truncate px-3 py-2" title={r.route}>{r.route || '—'}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.weight_diff_pct != null ? `${r.weight_diff_pct.toFixed(1)}%` : '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.days_between != null ? r.days_between.toFixed(1) : '—'}
                </td>
                {/* Сценарий = официальный сценарий кейса (source_scenario_id) как «Сцен. N» */}
                <td className="whitespace-nowrap px-3 py-2">
                  {r.sourceScenarioId != null ? (
                    <span className="inline-block rounded border border-[var(--accent-data)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--accent-data)]">
                      {r.sourceScenarioLabel}
                    </span>
                  ) : '—'}
                </td>
                {/* Совп. = сколько сценарных признаков совпало (scenario_like_score), НЕ номер сценария */}
                <td className="whitespace-nowrap px-3 py-2" title={t('analytics.table.matchedSigns')}>
                  {r.scenario_like_score != null ? `${r.scenario_like_score}/6` : '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {r.methods.length > 0 ? (
                    <span className="flex gap-1">
                      {r.methods.map(m => (
                        <span key={m} className="inline-block rounded bg-[var(--bg-surface)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                          {m}
                        </span>
                      ))}
                    </span>
                  ) : '—'}
                </td>
                <td className="max-w-[160px] truncate px-3 py-2 text-[var(--text-secondary)]" title={humanReasons(r.a_reasons ?? r.short_causes, t)}>
                  {humanReasons(r.a_reasons ?? r.short_causes, t) || '—'}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr><td colSpan={15} className="px-3 py-8 text-center text-[var(--text-tertiary)]">{t('analytics.table.nothingFound')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
            className="rounded-[4px] border border-[var(--border-default)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] disabled:opacity-40"
          >
            ← {t('common.action.back')}
          </button>
          <span className="text-[11px] text-[var(--text-tertiary)]">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
            className="rounded-[4px] border border-[var(--border-default)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] disabled:opacity-40"
          >
            {t('common.pagination.next')} →
          </button>
        </div>
      )}
    </div>
  )
}
